import { Configuration, LogLevel, PublicClientApplication } from '@azure/msal-browser';

declare global {
  interface Window {
    __CLIENT_ID__?: string;
    __TENANT_ID__?: string;
  }
}

// Read from window globals injected by CI/CD (via index.html placeholder substitution).
// Unsubstituted placeholder strings ("__CLIENT_ID__") fall back to local dev values.
// For local development, set REACT_APP_CLIENT_ID and REACT_APP_TENANT_ID in .env.local (gitignored).
// isPlaceholder returns boolean (not a type predicate narrowing to `v is undefined`)
// because it also returns true for non-empty placeholder strings like '__CLIENT_ID__'.
// Callers that need a narrowed type should handle undefined separately.
const isPlaceholder = (v: string | undefined): boolean =>
  !v || v.startsWith('__') || v.startsWith('%');

const windowObj = typeof window !== 'undefined' ? window : undefined;

const CLIENT_ID =
  (!isPlaceholder(windowObj?.__CLIENT_ID__) ? windowObj!.__CLIENT_ID__ : undefined) ??
  process.env.REACT_APP_CLIENT_ID ??
  '';

const TENANT_ID =
  (!isPlaceholder(windowObj?.__TENANT_ID__) ? windowObj!.__TENANT_ID__ : undefined) ??
  process.env.REACT_APP_TENANT_ID ??
  '';

// Guard: export an error string instead of throwing at module scope.
// A synchronous throw during import prevents index.tsx's .catch() from rendering the
// friendly error UI — the error would surface as an unhandled exception with no visible message.
// index.tsx reads msalConfigError and rejects inside Promise.all so the catch handler fires.
export const msalConfigError: string | null =
  !CLIENT_ID || !TENANT_ID
    ? 'MSAL configuration missing (CLIENT_ID or TENANT_ID is empty). ' +
      'Check CI/CD placeholder substitution or set REACT_APP_CLIENT_ID / REACT_APP_TENANT_ID in .env.local.'
    : null;

export const msalConfig: Configuration = {
  auth: {
    clientId: CLIENT_ID,
    authority: `https://login.microsoftonline.com/${TENANT_ID}`,
    redirectUri: typeof window !== 'undefined' ? window.location.origin : '/', // Lazy — safe in SSR/test environments
    navigateToLoginRequestUrl: false
  },
  cache: {
    cacheLocation: 'sessionStorage', // You can use sessionStorage or localStorage
    storeAuthStateInCookie: false, // Set to true for IE 11 or Edge Legacy
  },
  system: {
    loggerOptions: {
      loggerCallback: (level: LogLevel, message: string, containsPii: boolean) => {
        if (containsPii) return;
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            break;
          case LogLevel.Info:
            console.info(message);
            break;
          case LogLevel.Verbose:
            console.debug(message);
            break;
          case LogLevel.Warning:
            console.warn(message);
            break;
          default:
            break;
        }
      },
      logLevel: LogLevel.Info,
    },
  },
};

// Define the scopes you need
export const loginRequest = {
  scopes: ['api://fd598c5a-f486-4778-843c-401eafa4b7bd/loopback', 'User.Read'], // Microsoft Graph API permission
};

export const msalInstance = new PublicClientApplication(msalConfig);